import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ZoomIn } from 'lucide-react';

/**
 * BlogImage — click-to-enlarge figure wrapper (RULES_BLOG_POST click-to-enlarge rule).
 * Ported from raisy_FE/src/components/blog/BlogImage.tsx — same behavior, Playano
 * Education's dark/orange palette. Clicking opens a near-fullscreen lightbox
 * (~96-98vw/vh, object-contain, dimmed backdrop, Esc / click-to-close).
 */
export const BlogImage = ({
  src,
  alt,
  caption,
}: {
  src: string;
  alt: string;
  caption?: string;
}) => {
  const [open, setOpen] = useState(false);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, close]);

  return (
    <>
      <figure className="my-8">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="relative block w-full cursor-zoom-in rounded-xl overflow-hidden border border-[#333] group"
          aria-label={`Enlarge image: ${alt}`}
        >
          <img src={src} alt={alt} className="w-full" />
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity">
            <ZoomIn className="w-3.5 h-3.5" /> Enlarge
          </span>
        </button>
        {caption && (
          <figcaption className="mt-2 text-xs text-gray-400 text-center">
            {caption}
          </figcaption>
        )}
      </figure>

      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
            onClick={close}
            role="dialog"
            aria-modal="true"
            aria-label={alt}
          >
            <button
              type="button"
              onClick={close}
              className="absolute top-4 right-4 text-white/90 hover:text-white rounded-full p-2 bg-white/10 hover:bg-white/20 transition-colors"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={src}
              alt={alt}
              className="max-w-[98vw] max-h-[96vh] w-auto h-auto object-contain rounded-lg shadow-2xl"
            />
          </div>,
          document.body
        )}
    </>
  );
};
